/**
 * The vendor console's frame (container, phase 7b): its sub-navigation over
 * the section's page, shown only to a caller holding `vendor.ops` or
 * `vendor.admin` tenant-wide (`vendorAccessOf`). Everyone else — a tenant
 * admin, anyone on a node — gets a plain "not available" (the NAV entry is
 * hidden for them too; the server refuses every vendor route regardless).
 */
import { Outlet, useLocation, useNavigate } from '@tanstack/react-router';
import { useGrants, useMe } from '../app/hooks.js';
import { VendorNav } from '../components/vendor/VendorNav.js';
import { vendorAccessOf } from '../lib/vendor.js';

export function VendorLayout() {
  const me = useMe();
  const access = vendorAccessOf(useGrants());
  const pathname = useLocation({ select: (l) => l.pathname });
  const navigate = useNavigate();
  if (me.isPending) return <p className="p-8 text-center text-sm text-muted-foreground">Loading…</p>;
  if (!access.canSee) {
    return (
      <p className="mx-auto max-w-xl p-8 text-center text-sm text-muted-foreground" data-testid="vendor-unavailable">
        The vendor console needs vendor.ops or vendor.admin in the vendor’s own tenant.
      </p>
    );
  }
  return (
    <div className="mx-auto grid max-w-7xl gap-6" data-testid="vendor-area">
      <VendorNav pathname={pathname} onNavigate={(href) => void navigate({ to: href })} />
      <Outlet />
    </div>
  );
}

/** The write controls' gate, for the section pages. */
export function useVendorAdmin(): boolean {
  return vendorAccessOf(useGrants()).canAdmin;
}
